import React, { useEffect, useState } from 'react';

const API = '/api';

interface Item { titulo: string; cant: number; variante: string; }
interface Venta { id: number; fecha: string; total: number; comprador: string; envio: boolean; items: Item[]; }
interface Pregunta { id: number; fecha: string; texto: string; item: string; link: string | null; }
interface Aviso { t: string; tipo: string; topic?: string; error?: string; to?: string; plantilla?: boolean; motivo?: string; }
interface Resumen {
  cuenta: string | null; conectado: boolean; generado: string; errores: string[];
  ventas?: Venta[]; preguntas?: Pregunta[];
  publicaciones?: { activas: number; pausadas: number };
  reputacion?: { nivel: string | null; lider: string | null; ventas_completadas: number; reclamos?: number; demoras?: number; cancelaciones?: number };
  avisos?: Aviso[];
}

const TZ = 'America/Argentina/Buenos_Aires';
const plata = (n: number) => '$' + Math.round(n || 0).toLocaleString('es-AR');
const diaAR = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: TZ });
const horaAR = (iso: string) => new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const pct = (x?: number) => (x === undefined || x === null ? '-' : (x * 100).toFixed(1) + '%');

const NIVELES: { [k: string]: string } = {
  '5_green': '#16a34a', '4_light_green': '#65a30d', '3_yellow': '#ca8a04', '2_orange': '#ea580c', '1_red': '#dc2626',
};
const TIPO_AVISO: { [k: string]: string } = {
  aviso: '📩 Llegó aviso de ML', respuesta_ok: '💬 Pregunta respondida desde el panel', respuesta_error: '❌ No se pudo responder', whatsapp_ok: '✅ WhatsApp enviado', whatsapp_error: '❌ WhatsApp falló',
  whatsapp_pendiente: '⏸️ WhatsApp pendiente', error: '⚠️ Error', conectado: '🔗 Cuenta conectada', aviso_descartado: '🚫 Aviso descartado',
};
const TOPIC: { [k: string]: string } = {
  orders_v2: 'venta', questions: 'pregunta', messages: 'mensaje', items: 'publicación', shipments: 'envío', post_purchase: 'reclamo', claims: 'reclamo',
};

const card: React.CSSProperties = { background: '#1e293b', borderRadius: '12px', padding: '18px 20px', marginBottom: '20px' };
const kpi: React.CSSProperties = { background: '#1e293b', borderRadius: '12px', padding: '16px 18px', flex: '1 1 160px' };

export default function MercadoLibrePanel({ token }: { token: string }) {
  const [d, setD] = useState<Resumen | null>(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [vencida, setVencida] = useState(false);
  const [borradores, setBorradores] = useState<{ [id: number]: string }>({});
  const [enviando, setEnviando] = useState<{ [id: number]: string }>({});

  const responder = async (id: number) => {
    const texto = (borradores[id] || '').trim();
    if (!texto) return;
    setEnviando(e => ({ ...e, [id]: 'Enviando…' }));
    try {
      const r = await fetch(`${API}/ml/responder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id, texto })
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { setVencida(true); setEnviando(e => ({ ...e, [id]: '' })); return; }
      if (!r.ok) throw new Error(j.detail || r.status);
      setEnviando(e => ({ ...e, [id]: '✅ Respondida' }));
      setBorradores(b => { const n = { ...b }; delete n[id]; return n; });
      setTimeout(cargar, 1500);
    } catch (err: any) {
      setEnviando(e => ({ ...e, [id]: '❌ ' + String(err.message || err) }));
    }
  };
  const reingresar = () => { try { localStorage.removeItem('pc_token'); } catch (e) {} window.location.reload(); };

  const cargar = async () => {
    setCargando(true); setError('');
    try {
      const r = await fetch(`${API}/ml/resumen`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { setVencida(true); setCargando(false); return; }
      if (!r.ok) throw new Error(j.detail || r.status);
      setD(j);
    } catch (e: any) { setError(String(e.message || e)); }
    setCargando(false);
  };

  useEffect(() => { cargar(); const t = setInterval(cargar, 120000); return () => clearInterval(t); }, []);

  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  const ventas = d?.ventas || [];
  const ventasHoy = ventas.filter(v => diaAR(v.fecha) === hoy);
  const totalHoy = ventasHoy.reduce((a, v) => a + v.total, 0);
  const total7 = ventas.reduce((a, v) => a + v.total, 0);
  const preguntas = d?.preguntas || [];
  const rep = d?.reputacion;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#0f172a', margin: 0 }}>🛒 Mercado Libre</h2>
          <div style={{ color: '#475569', fontSize: '13px', marginTop: '4px' }}>
            {d ? <>Cuenta <b style={{ color: '#0f172a' }}>{d.cuenta || '-'}</b> · {d.conectado ? '🟢 conectada' : '🔴 desconectada'} · actualizado {horaAR(d.generado)}</> : 'Cargando…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a href="https://www.mercadolibre.com.ar/ventas/omni/listado" target="_blank" rel="noreferrer"
            style={{ padding: '8px 14px', background: '#334155', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontSize: '14px' }}>Abrir ventas en ML ↗</a>
          <button onClick={cargar} disabled={cargando}
            style={{ padding: '8px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
            {cargando ? 'Actualizando…' : '↻ Actualizar'}
          </button>
        </div>
      </div>

      {vencida && (
        <div style={{ ...card, color: '#fde68a', border: '1px solid #b45309', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>Tu sesión del panel venció. Volvé a entrar para ver los datos de Mercado Libre.</span>
          <button onClick={reingresar} style={{ padding: '8px 14px', background: '#f59e0b', color: '#0f172a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>Volver a entrar</button>
        </div>
      )}
      {error && <div style={{ ...card, color: '#fca5a5', border: '1px solid #7f1d1d' }}>No se pudo cargar: {error}</div>}
      {d && d.errores.length > 0 && (
        <div style={{ ...card, color: '#fcd34d', fontSize: '13px' }}>Algunos datos no se pudieron traer: {d.errores.join(' · ')}</div>
      )}

      {/* Indicadores */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div style={kpi}>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Ventas hoy</div>
          <div style={{ color: '#fff', fontSize: '26px', fontWeight: 700 }}>{ventasHoy.length}</div>
          <div style={{ color: '#22c55e', fontSize: '14px' }}>{plata(totalHoy)}</div>
        </div>
        <div style={kpi}>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Últimos 7 días</div>
          <div style={{ color: '#fff', fontSize: '26px', fontWeight: 700 }}>{ventas.length}</div>
          <div style={{ color: '#22c55e', fontSize: '14px' }}>{plata(total7)}</div>
        </div>
        <div style={{ ...kpi, border: preguntas.length ? '1px solid #f59e0b' : undefined }}>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Preguntas sin responder</div>
          <div style={{ color: preguntas.length ? '#fbbf24' : '#fff', fontSize: '26px', fontWeight: 700 }}>{preguntas.length}</div>
          <a href="https://www.mercadolibre.com.ar/preguntas/vendedor" target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: '13px' }}>Responder ↗</a>
        </div>
        <div style={kpi}>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Publicaciones</div>
          <div style={{ color: '#fff', fontSize: '26px', fontWeight: 700 }}>{d?.publicaciones?.activas ?? '-'}</div>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>activas · {d?.publicaciones?.pausadas ?? '-'} pausadas</div>
        </div>
        <div style={kpi}>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Reputación</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
            <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: NIVELES[rep?.nivel || ''] || '#475569', display: 'inline-block' }} />
            <span style={{ color: '#fff', fontWeight: 700 }}>{rep?.lider ? 'MercadoLíder ' + rep.lider : (rep?.nivel ? 'Nivel ' + rep.nivel.split('_')[0] : '-')}</span>
          </div>
          <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>
            Reclamos {pct(rep?.reclamos)} · Demoras {pct(rep?.demoras)} · Cancel. {pct(rep?.cancelaciones)}
          </div>
        </div>
      </div>

      {/* Preguntas */}
      {preguntas.length > 0 && (
        <div style={card}>
          <h3 style={{ color: '#fff', marginTop: 0 }}>❓ Preguntas sin responder</h3>
          {preguntas.map(q => (
            <div key={q.id} style={{ background: '#0f172a', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>{horaAR(q.fecha)} · {q.link ? <a href={q.link} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>{q.item}</a> : q.item}</div>
              <div style={{ color: '#fff', marginTop: '4px' }}>“{q.texto}”</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <textarea value={borradores[q.id] || ''} maxLength={2000} rows={2}
                  onChange={e => setBorradores(b => ({ ...b, [q.id]: e.target.value }))}
                  placeholder="Escribí la respuesta… (sale directo a Mercado Libre)"
                  style={{ flex: '1 1 320px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #334155', background: '#1e293b', color: '#fff', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical' }} />
                <button onClick={() => responder(q.id)} disabled={!(borradores[q.id] || '').trim() || enviando[q.id] === 'Enviando…'}
                  style={{ padding: '9px 16px', background: (borradores[q.id] || '').trim() ? '#22c55e' : '#334155', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                  Responder
                </button>
              </div>
              {enviando[q.id] && <div style={{ color: enviando[q.id].startsWith('❌') ? '#fca5a5' : '#86efac', fontSize: '13px', marginTop: '6px' }}>{enviando[q.id]}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Ventas */}
      <div style={card}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>💰 Ventas de los últimos 7 días</h3>
        {ventas.length === 0 && <div style={{ color: '#94a3b8' }}>{d ? 'Sin ventas en los últimos 7 días.' : '…'}</div>}
        {ventas.map(v => (
          <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', background: '#0f172a', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' }}>
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>{horaAR(v.fecha)} · {v.comprador || '-'} · {v.envio ? '📦 Con envío' : '🤝 Acordar entrega'}</div>
              {v.items.map((i, k) => (
                <div key={k} style={{ color: '#fff', marginTop: '3px' }}>{i.cant}× {i.titulo}{i.variante ? <span style={{ color: '#94a3b8' }}> ({i.variante})</span> : null}</div>
              ))}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#22c55e', fontWeight: 700, fontSize: '17px' }}>{plata(v.total)}</div>
              <a href={`https://www.mercadolibre.com.ar/ventas/${v.id}/detalle`} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: '13px' }}>Ver detalle ↗</a>
            </div>
          </div>
        ))}
      </div>

      {/* Avisos */}
      <div style={card}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>🔔 Últimos avisos recibidos y enviados</h3>
        {(d?.avisos || []).length === 0 && <div style={{ color: '#94a3b8' }}>Todavía no hay avisos.</div>}
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {(d?.avisos || []).map((a, k) => (
            <div key={k} style={{ display: 'flex', gap: '12px', padding: '6px 4px', borderBottom: '1px solid #334155', fontSize: '13px' }}>
              <span style={{ color: '#64748b', minWidth: '92px' }}>{horaAR(a.t)}</span>
              <span style={{ color: a.tipo.includes('error') ? '#fca5a5' : '#e2e8f0' }}>
                {TIPO_AVISO[a.tipo] || a.tipo}{a.topic ? ` (${TOPIC[a.topic] || a.topic})` : ''}{a.plantilla ? ' · plantilla' : ''}{a.error ? ` — ${a.error}` : ''}{a.motivo ? ` — ${a.motivo}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
